import React, { FC, ReactNode } from 'react';
import { ExpandIcon } from '../SvgIcon';
import { clsx } from 'clsx';
type SectionBaseProps = {
  children: ReactNode;
};

interface AppSectionProps extends SectionBaseProps {
  title: string;
  menu?: ReactNode;
  isOpen?: boolean;
}

type SectionMenuProps = {
  menu: ReactNode;
};

// Removed px-5 and this might break webapp profile formatting.
// Add a margin to profile components if I want the spacing.
const PageWrapper: FC<SectionBaseProps> = ({ children }) => {
  return (
    <div className="flex container flex-col px-2 sm:px-5 md:px-5 xl:px-0 mx-auto max-w-6xl mt-1">
      {children}
    </div>
  );
};

const AppSectionBasic: FC<SectionBaseProps> = ({ children }) => {
  return (
    <div className="card rounded-xl bg-base-200 border border-accent/40 p-4 sm:p-6 mb-5">
      {children}
    </div>
  );
};

const AppSection: FC<AppSectionProps> = ({ children }) => {
  // const {title} = props;
  return (
    <AppSectionBasic>
      <h2 className="font-medium leading-none text-secondary"> </h2>
      {children}
    </AppSectionBasic>
  );
};

const AppProfileSection: FC<SectionBaseProps> = ({ children }) => {
  return (
    <div className="card rounded-xl border border-base-300 mb-5 p-4 bg-white">
      <div className="flex gap-4">{children}</div>
    </div>
  );
};

const AppSectionBasicRow: FC<SectionBaseProps> = ({ children }) => {
  return (
    <div className="mb-5 w-full">
      <div className="flex flex-col md:flex-row gap-5">{children}</div>
    </div>
  );
};
const AppSectionHalfRow: FC<AppSectionProps> = ({ children, title, menu }) => {
  return (
    <div className="flex flex-col basis-1/2 bg-base-200 border border-accent/40 rounded-lg p-4 sm:p-6">
      <span className="flex flex-row justify-between">
        <h2 className="font-medium leading-none text-secondary">{title}</h2>
        <SectionMenu menu={menu} />
      </span>

      {children}
    </div>
  );
};

// Quarter of a row section with title props
const AppSectionQuarterRow: FC<AppSectionProps> = ({
  children,
  title,
  menu,
}) => {
  return (
    <div className="flex flex-col basis-1/4 bg-base-200 border border-accent/40 rounded-lg p-4 sm:p-6">
      <span className="flex flex-row justify-between">
        <h2 className="font-medium leading-none text-secondary">{title}</h2>
        <SectionMenu menu={menu} />
      </span>

      {children}
    </div>
  );
};

// 3/4 of a row section with title props
const AppSection34Row: FC<AppSectionProps> = ({ children, title, menu }) => {
  return (
    <div className="flex flex-col basis-full lg:basis-3/4 border border-accent/40 bg-base-200 rounded-lg p-4 sm:p-6">
      <div className="flex flex-row justify-between">
        <h2 className="font-medium leading-none text-secondary">{title}</h2>
        {menu && <div className="ml-auto mr-0">{menu}</div>}
      </div>

      {children}
    </div>
  );
};

// Quarter section, no title props
const SectionQuarterRow: FC<SectionBaseProps> = ({ children }) => {
  return (
    <div className="flex flex-col basis-1/4 border border-base-300 rounded-md p-4">
      {children}
    </div>
  );
};

// Half section, no title props
const SectionHalfRow: FC<SectionBaseProps> = ({ children }) => {
  return (
    <div className="flex flex-col basis-1/2 border border-base-300 rounded-md p-4">
      {children}
    </div>
  );
};
// One-third section, no title props
const Section13Row: FC<SectionBaseProps> = ({ children }) => {
  return (
    <div className="flex flex-col basis-1/3 border border-base-300 rounded-md p-4">
      {children}
    </div>
  );
};

// Two-thirds section, no title props
const Section23Row: FC<SectionBaseProps> = ({ children }) => {
  return (
    <div className="flex flex-col basis-2/3 border border-base-300 rounded-md p-4">
      {children}
    </div>
  );
};

// 3/4 section section, no title props
const Section34Row: FC<SectionBaseProps> = ({ children }) => {
  return (
    <div className="flex flex-col basis-3/4 border border-base-300 rounded-md p-4">
      {children}
    </div>
  );
};

const ModuleRow: FC<AppSectionProps> = ({ children, title, menu }) => {
  return (
    <AppSectionBasic>
      <div className="flex flex-row justify-between">
        <h2 className="font-medium leading-none text-secondary">{title}</h2>
        {menu && <div className="ml-auto mr-0">{menu}</div>}
      </div>
      <div className="flex flex-col gap-5 my-5">{children}</div>
    </AppSectionBasic>
  );
};

// PROFILE
const ProfileWrapper: FC<SectionBaseProps> = ({ children }) => {
  return (
    <div className="hidden lg:flex lg:col-span-1">
      <div className="w-full flex flex-col bg-base-200 border border-accent/40 rounded-lg px-4 pt-2 pb-2 sm:px-6 min-h-[calc(100vh-8rem)]">
        {children}
      </div>
    </div>
  );
};
// PROFILE MOBILE
const ProfileMenuMobile: FC<SectionBaseProps> = ({ children }) => {
  return (
    <div className="md:hidden w-full flex flex-row bg-base-200 border border-accent/40 rounded-lg">
      {children}
    </div>
  );
};

const ProfileSectionQuarterRowTitle: FC<AppSectionProps> = ({
  children,
  title,
  menu,
}) => {
  return (
    <div className="flex flex-col basis-1/4 bg-base-200 border border-accent/40 rounded-lg p-4 sm:p-6">
      <span className="flex flex-row justify-between">
        <h2 className="font-medium leading-none text-secondary">{title}</h2>
        <SectionMenu menu={menu} />
      </span>
      <div className="flex ">{children}</div>
    </div>
  );
};
const ProfileSectionRow: FC<AppSectionProps> = ({ children, title, menu }) => {
  return (
    <div className="flex flex-col basis-3/4 bg-base-200 border border-accent/40 rounded-lg p-4 sm:p-6">
      <span className="flex flex-row justify-between">
        <h2 className="font-medium leading-none text-secondary">{title}</h2>
        <SectionMenu menu={menu} />
      </span>
      <div className="flex ">{children}</div>
    </div>
  );
};

const ProfileSectionCollapse: FC<AppSectionProps> = ({
  children,
  title,
  isOpen,
}) => {
  return (
    <div
      className={clsx(
        'collapse collapse-arrow bg-base-200 rounded-lg border border-accent/40 w-full',
        { 'force-open': isOpen },
      )}
    >
      <input type="checkbox" className="peer" />
      <div className="collapse-title font-semibold text-secondary">{title}</div>
      <div className="collapse-content">{children}</div>
    </div>
  );
};

const SettingsSectionCollapse: FC<AppSectionProps> = ({ children, title }) => {
  return (
    <div className="collapse collapse-arrow bg-base-200 rounded-lg border border-accent/40 w-full">
      <input type="checkbox" className="peer" />
      <div className="collapse-title font-semibold text-secondary">{title}</div>
      <div className="collapse-content">{children}</div>
    </div>
  );
};

const SectionMenu: FC<SectionMenuProps> = ({ menu }) => {
  return <div>{menu}</div>;
};

const WebAppGrid: FC<SectionBaseProps> = ({ children }) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 lg:gap-4 grid-flow-row mb-8">
      {children}
    </div>
  );
};

export {
  PageWrapper,
  AppSectionBasic,
  AppSectionBasicRow,
  AppSection,
  AppProfileSection,
  AppSectionHalfRow,
  AppSectionQuarterRow,
  AppSection34Row,
  SectionQuarterRow,
  SectionHalfRow,
  Section13Row,
  Section23Row,
  Section34Row,
  ModuleRow,
  ProfileWrapper,
  ProfileSectionQuarterRowTitle,
  ProfileSectionRow,
  ProfileMenuMobile,
  ProfileSectionCollapse,
  SettingsSectionCollapse,
  WebAppGrid,
};
